<template>
  <!-- Rebuilt on the KarroFin components. The page keeps every call it made
       before -- same endpoint, same params, same response shape; only the
       presentation moved. -->
  <div class="kf-page">
    <!-- Title-less on purpose: the navbar already says "Team". What is left of
         the header is the action row. The old markup put the click handler on a
         paragraph next to the button, so only the words were clickable and the
         round `+` itself did nothing. -->
    <PageHeader>
      <v-btn
        color="primary"
        depressed
        class="text-none kf-cta"
        @click="openAddDialog"
      >
        <LucideIcon name="plus" :size="20" class="mr-1" />
        Add member
      </v-btn>
    </PageHeader>

    <!-- Search used to live in the navbar and reach this page through the
         shared Vuex `search_text`. It is a page filter, so it belongs with the
         page's filters; the route now sets `show_global_search: false` so the
         navbar box does not appear alongside this one. Debounce is the bar's
         own (350ms) -- there is deliberately no watcher here. -->
    <FilterBar
      :search.sync="search_query"
      :show-date="false"
      :show-reset="false"
      search-placeholder="Search by name, mobile or email"
      @change="onSearch"
    />

    <DataTable
      title="Team"
      :headers="headers"
      :items="user_data_list"
      :loading="table_loading"
      :page="page_number"
      :total-pages="total_page_count"
      :total-count="null"
      :page-length="$keys.PAGE_LENGTH"
      empty-title="No team members match this search"
      empty-message="Try a different name, mobile number or email."
      empty-icon="users"
      @page-change="onPage"
    >
      <!-- Which screens this member is restricted on, so an admin can see who
           is limited without opening every row. The filter values themselves
           stay in the edit dialog. -->
      <template #[`item.filtered_screens`]="{ item }">
        <span v-if="!item.filtered_screens || !item.filtered_screens.length"
              class="grey--text">—</span>
        <v-chip
          v-for="screen in item.filtered_screens"
          :key="screen"
          class="mr-1"
          outlined
          small
        >
          {{ screen }}
        </v-chip>
      </template>

      <template #[`item.action`]="{ item }">
        <v-btn
          icon
          small
          class="mr-1"
          title="Edit member"
          @click.stop="openEditDialog(item.id)"
        >
          <LucideIcon name="pencil" :size="18" />
        </v-btn>
        <v-btn
          icon
          small
          title="Remove member"
          @click.stop="openDeleteDialog(item)"
        >
          <LucideIcon name="trash-2" :size="18" />
        </v-btn>
      </template>
    </DataTable>

    <!-- add/edit dialog -->
    <AddMemberDialog
      ref="add_member_dialog"
      :dialog="dialog_data"
      @update="getTeammateList"
    ></AddMemberDialog>
    <!-- delete dialog -->
    <DeleteDialog :dialog="delete_dialog" @update="getTeammateList"></DeleteDialog>
  </div>
</template>
<script>
import PageHeader from "@/components/karrofin/PageHeader.vue";
import FilterBar from "@/components/karrofin/FilterBar.vue";
import DataTable from "@/components/karrofin/DataTable.vue";

export default {
  components: {
    PageHeader,
    FilterBar,
    DataTable,
    AddMemberDialog: () => import("@/components/team/AddMemberDialog"),
    DeleteDialog: () => import("@/components/team/DeleteDialog"),
  },
  data() {
    return {
      table_loading: false,
      page_number: 1,
      total_page_count: 2,
      /* Owned by the page now rather than read from the shared navbar search. */
      search_query: "",
      user_data_list: [],
      /* Widths are the KarroFin convention: a fixed budget of about 1100px --
         a 1366px laptop less the 200px drawer and the page gutter -- so the
         columns land in the same places they do on every other screen. */
      headers: [
        { text: "Name", value: "name", width: 260 },
        { text: "Mobile No.", value: "mobile", width: 180 },
        { text: "Email", value: "email", width: 260 },
        { text: "Filtered view", value: "filtered_screens", width: 200 },
        { text: "", value: "action", align: "end", width: 120 },
      ],
      /*dialog data*/
      dialog_data: {
        flag: false,
        title: "",
        is_edit: false,
        loading: false,
      },
      delete_dialog: {
        flag: false,
        id: "",
        name: "",
      },
    };
  },
  created() {
    this.getTeammateList(true);
  },
  methods: {
    /* fetching the customer list form server*/
    getTeammateList(isCancel = false) {
      const self = this;
      self.table_loading = true;
      self.toggle_loading();
      let params = {
        page_number: this.page_number,
        page_length: this.$keys.PAGE_LENGTH,
        search_query: this.search_query,
      };
      const successHandler = (response) => {
        if (response.data.success) {
          /* This endpoint returns its rows at the top level rather than under
             `result`, unlike the other Operations screens. */
          self.user_data_list = response.data.user_data;
          self.total_page_count = response.data.total_page_count;
        }
      };
      const finallyHandler = () => {
        self.table_loading = false;
        // self.toggle_loading()
      };

      self.request_GET(
        self,
        self.$urls.TEAMMATE_LIST,
        params,
        successHandler,
        null,
        null,
        finallyHandler,
        isCancel
      );
    },
    /* Search does not reset the page number -- same as before the rebuild. */
    onSearch() {
      this.getTeammateList(true);
    },
    /* The old pager was `@input="getTeammateList"`, which handed the page
       number straight to the `isCancel` parameter. Kept as it was: this is a
       visual rebuild, and that argument is carried across untouched. */
    onPage(page) {
      this.page_number = page;
      this.getTeammateList(page);
    },
    /* Open the Add member dialog form */
    openAddDialog() {
      this.dialog_data.title = this.$lang.ADD_MEMBER;
      this.dialog_data.is_edit = false;
      this.dialog_data.flag = true;
      setTimeout(() => {
        this.$refs.add_member_dialog.resetForm();
        this.$refs.add_member_dialog.id = null;
        this.$refs.add_member_dialog.getMemberDetails(null);
      }, 80);
    },
    /*Open the edit dialog form*/
    openEditDialog(id) {
      this.dialog_data.title = this.$lang.EDIT_MEMBER;
      this.$refs.add_member_dialog.getMemberDetails(id);
      this.dialog_data.is_edit = true;
      this.dialog_data.flag = true;
    },
    openDeleteDialog(item) {
      this.delete_dialog.id = item.id;
      this.delete_dialog.name = item.name;
      this.delete_dialog.flag = true;
    },
  },
};
</script>

<style scoped>
/* Doubled class to beat Vuetify's own button rules without relying on which
   sheet loads last -- the same trick offers/index.vue uses for its CTA. */
.kf-cta.v-btn {
  letter-spacing: 0;
  font-weight: 600;
}
</style>
